import cn from '@integrations/classnames'
import { formatDisplayDate } from '@utils'
import { getTransactionStatusName, TransactionDirections, useCurrencies } from 'mmfintech-backend-api'
import type { TransactionViewPortal } from 'mmfintech-backend-api/types'
import { fetchLastChunk, formatMoney, formatTime, tr } from 'mmfintech-commons'
import { MMTableView } from 'mmfintech-portal-commons'

interface Props {
  transactions: TransactionViewPortal[]
  onRowClick?: (transaction: TransactionViewPortal) => void
}

function RecurringPastTransactionsTable({ transactions, onRowClick = () => {} }: Props) {
  const { getCurrencyPrecision } = useCurrencies()

  return (
    <MMTableView.Table>
      <MMTableView.Head>
        <MMTableView.Row>
          <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.ID', 'ID')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE', 'Time')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.REFERENCE', 'Reference')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.ACCOUNT', 'Account')}</MMTableView.HeadCell>
          <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}</MMTableView.HeadCell>
          <MMTableView.HeadCell className={cn('thin')}>
            {tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status')}
          </MMTableView.HeadCell>
        </MMTableView.Row>
      </MMTableView.Head>

      <MMTableView.Body>
        {transactions.map((transaction) => {
          const { id, reqTime, trnTypeLocalizationKey, reference, accountName, amount, currency, direction, status } =
            transaction

          return (
            <MMTableView.Row
              key={id}
              className={cn('recurring-past-transactions-row')}
              onClick={() => onRowClick(transaction)}
              data-test='recurring-past-transactions-row'
            >
              <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.ID', 'ID')}>{id}</MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE', 'Time')} className={cn('no-wrap')}>
                <div>
                  <span className={cn('text-nowrap')}>{formatDisplayDate(reqTime)}</span>{' '}
                  <span className={cn('text-nowrap')}>{formatTime(reqTime)}</span>
                </div>
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}>
                {tr(trnTypeLocalizationKey, fetchLastChunk(trnTypeLocalizationKey))}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.REFERENCE', 'Reference')}>
                {reference}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.ACCOUNT', 'Account')}>
                {accountName}
              </MMTableView.Cell>
              <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}>
                <span className={cn('amount')}>
                  {formatMoney(
                    amount * (direction === TransactionDirections.WITHDRAW ? -1 : 1),
                    currency,
                    getCurrencyPrecision(currency)
                  )}
                </span>
              </MMTableView.Cell>
              <MMTableView.Cell
                caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status')}
                className={cn('status-cell')}
              >
                <span className={cn('status', `status-${status.toLowerCase()}`)}>
                  {getTransactionStatusName(status)}
                </span>
              </MMTableView.Cell>
            </MMTableView.Row>
          )
        })}
      </MMTableView.Body>
    </MMTableView.Table>
  )
}

export default RecurringPastTransactionsTable
